import { useEffect, useRef, useCallback } from "react";

/**
 * Blue custom cursor — ign-prestige-digital reference.
 *
 * Activation: `(hover: hover) and (pointer: fine)` media query + viewport ≥ 1024.
 * NOTE: we do NOT check `"ontouchstart" in window` because modern touchscreen
 * laptops expose that property while still having a fine pointer.
 *
 * The class `.cursor-ready` on <html> triggers a CSS rule that hides ALL
 * native cursors. If this component fails to mount, the class is never
 * added → native cursor stays visible (safe fallback).
 */
export default function Cursor() {
    const dotRef = useRef(null);
    const ringRef = useRef(null);

    const init = useCallback(() => {
        const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
        if (!mq.matches || window.innerWidth < 1024) return;

        const dot = dotRef.current;
        const ring = ringRef.current;
        if (!dot || !ring) return;

        /* ---- hide native cursor via class ---- */
        document.documentElement.classList.add("cursor-ready");

        let mx = -100, my = -100;
        let rx = -100, ry = -100;
        let raf;

        /* Dot follows pointer instantly; ring lags behind smoothly */
        const onPointerMove = (e) => {
            mx = e.clientX;
            my = e.clientY;
            dot.style.transform = `translate(${mx}px, ${my}px) translate(-50%, -50%)`;
        };

        const loop = () => {
            rx += (mx - rx) * 0.15;
            ry += (my - ry) * 0.15;
            ring.style.transform = `translate(${rx}px, ${ry}px) translate(-50%, -50%)`;
            raf = requestAnimationFrame(loop);
        };

        /* Expand ring on interactive elements */
        const onPointerOver = (e) => {
            const hit = e.target.closest(
                "a, button, input, textarea, select, label, [data-hover], [data-magnetic], [role='button'], summary"
            );
            const on = !!hit;
            ring.classList.toggle("is-hover", on);
            dot.classList.toggle("is-hover", on);
        };

        /* Slight press feedback */
        const onDown = () => { ring.classList.add("is-press"); };
        const onUp   = () => { ring.classList.remove("is-press"); };

        /* Hide when pointer leaves the viewport */
        const onLeave = () => { dot.style.opacity = "0"; ring.style.opacity = "0"; };
        const onEnter = () => { dot.style.opacity = "1"; ring.style.opacity = "1"; };

        window.addEventListener("pointermove", onPointerMove, { passive: true });
        document.addEventListener("pointerover", onPointerOver, { passive: true });
        window.addEventListener("pointerdown", onDown);
        window.addEventListener("pointerup", onUp);
        document.documentElement.addEventListener("pointerleave", onLeave);
        document.documentElement.addEventListener("pointerenter", onEnter);
        raf = requestAnimationFrame(loop);

        return () => {
            document.documentElement.classList.remove("cursor-ready");
            window.removeEventListener("pointermove", onPointerMove);
            document.removeEventListener("pointerover", onPointerOver);
            window.removeEventListener("pointerdown", onDown);
            window.removeEventListener("pointerup", onUp);
            document.documentElement.removeEventListener("pointerleave", onLeave);
            document.documentElement.removeEventListener("pointerenter", onEnter);
            cancelAnimationFrame(raf);
        };
    }, []);

    useEffect(() => {
        return init();
    }, [init]);

    return (
        <>
            <div ref={dotRef} className="cursor-dot" aria-hidden="true" />
            <div ref={ringRef} className="cursor-ring" aria-hidden="true" />
        </>
    );
}
